/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { Link, Button, Grid, TextField, Typography, Box, FormControl, Divider, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';

import InitialStoreSelector from './InitialStoreSelector';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { SignIn, SignUp, Waitlist, useUser } from '@clerk/clerk-react';
import { isDemoModeEligible, enableDemoMode, disableDemoMode, isDemoModeEnabled } from '../../../utils/featureAccess';

const LoginForm = (props: { switchDisplay: () => void }) => {
  const { t } = useTranslation('authentication');
  const { t: t2 } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showClerkSignIn, setShowClerkSignIn] = useState(false);
  const [showClerkWaitList, setShowClerkWaitList] = useState(false);
  const [showClerkSignUp, setShowClerkSignUp] = useState(false);
  const [demoMode, setDemoMode] = useState(() => isDemoModeEnabled());
  const { isSignedIn, user, isLoaded } = useUser();

  const showDemoToggle = isDemoModeEligible(email, window.location.hostname);

  // Use the useNswagClient hook for API calls
  const extractParamsFromHashOrSearch = (searchOrHash: string) => {
    const params = new URLSearchParams(searchOrHash.replace(/^#/, ''));
    return {
      error: params.get('error'),
      error_description: decodeURIComponent(params.get('error_description') ?? ''),
      access_token: params.get('access_token'),
      // Add Clerk activation parameters
      __clerk_status: params.get('__clerk_status'),
      __clerk_ticket: params.get('__clerk_ticket'),
    };
  };

  useEffect(() => {
    // Check URL hash for parameters
    const hashParams = extractParamsFromHashOrSearch(location.hash);
    // Check URL search params for parameters
    const searchParams = extractParamsFromHashOrSearch(location.search);

    // Combine both sources, giving priority to search params
    const allParams = { ...hashParams, ...searchParams };

    if (allParams.error) {
      setSnackbarMessage(allParams.error_description ?? '');
      setOpenSnackbar(true);
    }

    // Handle Clerk activation link
    if (allParams.__clerk_ticket) {
      // User is coming from an activation/invitation link
      // Show the Clerk SignIn component which will handle the ticket verification
      if (allParams.__clerk_status === 'sign_in') {
        setShowClerkSignIn(true);
      } else if (allParams.__clerk_status === 'sign_up') {
        setShowClerkSignUp(true);
      }
    }
  }, [location.hash, location.search]);

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
    setEmailError('');

    // Reset demo mode whenever the email changes to avoid carrying it across identities.
    if (demoMode) {
      setDemoMode(false);
      disableDemoMode();
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setTimeout(() => {
      setSnackbarMessage('');
    }, 300);
  };

  const handleLoginButtonClick = async (e: any) => {
    e.preventDefault();

    if (!email) {
      setEmailError('Enter your email');
      return;
    }

    setEmailError('');

    // Persist demo mode choice before handing off to Clerk
    if (demoMode) {
      enableDemoMode();
    } else {
      disableDemoMode();
    }

    setShowClerkSignIn(true);
  };

  const handleSignUpButtonClick = (e: any) => {
    e.preventDefault();
    setShowClerkWaitList(true);
  };

  const getCustomTheme = () => {
    return {
      elements: {
        // This is the container/card for the component
        card: {
          boxShadow: 'none',
          border: 'none',
          padding: '1px',
          width: '360px',
        },
        cardBox: {
          boxShadow: 'none',
          border: 'none',
          borderRadius: 0,
          width: 'auto',
        },
        footerAction: {
          display: 'none',
        },
        formButtonPrimary: {
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          padding: '12px 18px',
          height: '48px',
          lineHeight: '1.5rem',
          fontSize: '1rem',
          transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          borderRadius: '8px',
          border: '1px solid',
          fontWeight: '600',
          boxShadow: 'none !important',
        },
        formFieldAction: {
          transform: 'translate(336px, 70px) scale(1.3)',
          position: 'absolute',
          width: '100px',
        },
        logoImage: {
          display: 'none', // Hide the logo image
        },
        headerTitle: {
          margin: 0,
          lineHeight: '2.375rem',
          fontSize: '1.875rem',
          fontWeight: 600,
          textAlign: 'center',
        },
        headerSubtitle: {
          lineHeight: '1.5rem',
          fontSize: '1rem',
          //display: 'none', // Hide the header subtitle
        },
        formFieldLabelRow: {
          position: 'absolute',
          transform: 'translate(-4px, -12px) scale(0.75)',
          background: 'white',
          padding: '0 6px',
          left: 0,
          top: 0,
          zIndex: 1,
        },
        formFieldLabel: {
          fontWeight: 400,
          fontSize: '1.0rem',
          lineHeight: 1.38462,
          color: 'rgba(0, 0, 0, 0.6)',
        },
        formFieldInput: {
          fontWeight: 400,
          height: '40px',
          padding: '8.5px 14px',
          borderRadius: '4px',
          maxHeight: 'unset',
          width: 'unset',
          fontSize: '0.8125rem',
          lineHeight: 1.38462,
          borderWidth: '1px !important',
          boxShadow: 'none !important',
          '&:-internal-autofill-selected': {
            backgroundColor: 'transparent !important',
          },
        },
        footer: {
          display: 'none', // Hide the footer
        },
      },
    };
  };

  if (isSignedIn && isLoaded && user) {
    return <InitialStoreSelector />;
  }

  if (showClerkWaitList) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignContent: 'center',
          width: '360px',
        }}
      >
        <Waitlist
          appearance={getCustomTheme()} />
        <Link
          component="button"
          onClick={() => setShowClerkWaitList(false)}
          sx={{
            alignSelf: 'flex-start',
            mb: 2,
            mt: 8,
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          ← {t2('back')}
        </Link>
      </Box>
    );
  }

  if (showClerkSignIn) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignContent: 'center',
          width: '360px',
        }}
      >
        <SignIn initialValues={{ emailAddress: email }}
          forceRedirectUrl={window.location.pathname + window.location.search}
          appearance={getCustomTheme()} />
        <Link
          component="button"
          onClick={() => setShowClerkSignIn(false)}
          sx={{
            alignSelf: 'flex-start',
            mb: 2,
            mt: 8,
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          ← {t2('back')}
        </Link>
      </Box>
    );
  }

  if (showClerkSignUp) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignContent: 'center',
          width: '360px',
        }}
      >
        <SignUp
          appearance={getCustomTheme()} />
        <Link
          component="button"
          onClick={() => setShowClerkSignUp(false)}
          sx={{
            alignSelf: 'flex-start',
            mb: 2,
            mt: 8,
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          ← {t2('back')}
        </Link>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        width: '360px',
      }}
    >
      <Typography variant="displaySM"
        fontWeight={600}
        sx={{ textAlign: 'center' }}>
        {t('title')}
      </Typography>
      <Typography color="textSecondary"
        variant="textMD"
        sx={{ textAlign: 'center', mt: 2 }}>
        {t('welcomeMessage')}
      </Typography>
      <Box component="form"
        onSubmit={(e) => e.preventDefault()}
        autoComplete="on">
        <Box sx={{ xs: 12, mt: 8 }}>
          <FormControl fullWidth>
            <TextField
              name="email"
              InputLabelProps={{ shrink: true }}
              value={email}
              onChange={handleEmailChange}
              autoComplete="email"
              label={t('email')}
              error={!!emailError}
              helperText={emailError}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                },
              }}
              required
              fullWidth
            />
          </FormControl>
        </Box>
      </Box>
      {showDemoToggle && (
        <Box sx={{ m: '8px', mt: 0, px: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={demoMode}
                onChange={(e) => setDemoMode(e.target.checked)}
                size="small"
                color="primary"
              />
            }
            label={
              <Typography variant="textSM"
                color="textSecondary">
                Demo Mode
              </Typography>
            }
          />
        </Box>
      )}
      <Grid sx={{ pt: 4 }}>
        <Button
          type="button"
          variant="primary"
          fullWidth
          onClick={handleLoginButtonClick}
        >
          {t('signIn')}
        </Button>
      </Grid>
      <Box sx={{ width: '100%', mt: 8 }}>
        <Divider>
          <Typography variant="textSM"
            color="textSecondary"
            sx={{ mx: 2 }}>
            OR
          </Typography>
        </Divider>
      </Box>
      <Grid sx={{ pt: 8 }}>
        <Button
          type="button"
          variant="primary"
          fullWidth
          onClick={handleSignUpButtonClick}
        >
          {t('signUp')}
        </Button>
      </Grid>
      <MessagesSnackbar open={openSnackbar}
        onClose={handleCloseSnackbar}
        message={snackbarMessage?.trim() ? snackbarMessage : t('invalidlogin')}
        severity='error'
        duration={null} />
    </Box>
  );
};

export default LoginForm;
